import React, { ChangeEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { RootState, AppDispatch } from "../store/store";
import { deleteHealthCondition, fetchUserData } from "../store/UserSlice";
import { useTranslation } from "react-i18next";

import Popup from "../components/Popup";
import PopupContentEdit from "../components/Forms/HealthConditionEdit";
import DeleteConfirmation from "../components/ConfirmBox";

import { IHealthCondition } from "../interfaces/HealthCondition";

import "react-toastify/dist/ReactToastify.css";

export default function Dashboard() {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isPopupOpenForEdit, setIsPopupOpenForEdit] = useState(false);
  const [changeText, setChangeText] = useState(true);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [editedHealthConditionId, setEditedHealthConditionId] =
    useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");

  const { data, loading, error } = useSelector(
    (state: RootState) => state.user
  );

  /**
   * Fetch user data when dashboard loads.
   */
  useEffect(() => {
    dispatch(fetchUserData());
  }, [dispatch]);

  /**
   * Open add condition popup.
   */
  const openPopup = () => {
    setIsPopupOpen(true);
  };

  /**
   * Close add condition popup.
   */
  const closePopup = () => {
    setIsPopupOpen(false);
  };

  /**
   * Open edit condition popup.
   */
  const openEditPopup = (healthConditionId: string) => {
    setEditedHealthConditionId(healthConditionId);
    setIsPopupOpenForEdit(true);
  };

  /**
   * Close edit condition popup.
   */
  const closeEditPopup = () => {
    setIsPopupOpenForEdit(false);
    setEditedHealthConditionId(null);
  };

  /**
   * Handle search input.
   */
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setChangeText(e.target.value === "");
    setSearchQuery(e.target.value);
  };

  /**
   * Delete health condition.
   */
  const handleDelete = async (healthConditionId: string) => {
    try {
      await dispatch(deleteHealthCondition(healthConditionId)).unwrap();

      toast.success(t("dashboard.deleteSuccess"));

      setDeleteItemId(null);

      // Refresh user data after successful deletion.
      await dispatch(fetchUserData()).unwrap();
    } catch (error) {
      console.error("Failed to delete health condition:", error);
      toast.error(t("dashboard.deleteFailed"));
    }
  };

  /**
   * Cancel delete confirmation.
   */
  const handleCancel = () => {
    setDeleteItemId(null);
  };

  /**
   * Normalize user data into a health-condition array.
   *
   * Supports:
   * - data being an array
   * - data being an object with healthConditions
   * - data being null/undefined
   */
  const getHealthConditions = (): IHealthCondition[] => {
    if (!data) {
      return [];
    }

    if (typeof data === "string") {
      try {
        const parsedData = JSON.parse(data);

        if (Array.isArray(parsedData)) {
          return parsedData;
        }

        return parsedData?.healthConditions ?? [];
      } catch (error) {
        console.error("Failed to parse user data:", error);
        return [];
      }
    }

    if (Array.isArray(data)) {
      return data;
    }

    return data?.healthConditions ?? [];
  };

  const healthConditionsList = getHealthConditions();
  const filteredConditions = healthConditionsList.filter((item) =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /**
   * Only show the full-page loader while the initial
   * user data request is loading.
   */
  if (loading) {
    return (
      <div className="flex items-center justify-center flex-1 w-full min-h-[300px]">
        <div
          className="border-t-4 border-solid h-12 w-12 border-b-4 border-r-4 border-l-4 rounded-full animate-spin"
          style={{ borderColor: "#B4804A" }}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <h2 className="py-1 pb-3 sm:pb-4 text-xl sm:text-2xl font-gilroy font-semibold text-[#2b404b] shrink-0">
        {t("dashboard.healthConditions")}
      </h2>

      <div className="bg-white flex-1 min-h-[380px] overflow-hidden flex flex-col rounded-md shadow-sm">
        <div className="flex flex-col sm:flex-row p-4 sm:p-6 pb-3 sm:pb-4 gap-3 sm:gap-4 shrink-0">
          <form className="flex items-start w-full sm:w-auto sm:flex-1 max-w-md" onSubmit={(e) => e.preventDefault()}>
            <div className="relative w-full">
              <input
                type="text"
                placeholder=""
                onChange={handleInputChange}
                className="border border-gray-200 rounded-md py-2 w-full pl-10 pr-4 focus:outline-none placeholder-[#B0BEC5] bg-[#F5F7F8] text-sm"
              />

              {changeText && (
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <img
                    src="./images/Vector.png"
                    className="w-4 h-4 mr-2"
                    alt="search"
                  />

                  <span className="text-sm text-[#B0BEC5]">
                    {t("dashboard.search")}
                  </span>
                </div>
              )}
            </div>
          </form>

          <Link
            to=""
            className="bg-[#B4804A] hover:bg-[#9d6f3e] transition-colors flex sm:ml-auto py-2.5 px-4 justify-center text-center items-center rounded-lg font-gilroy font-bold text-white text-sm whitespace-nowrap shadow-xs w-full sm:w-auto"
            onClick={(e) => {
              e.preventDefault();
              openPopup();
            }}
          >
            <img
              src="./images/plus.png"
              className="pr-2 w-5 h-5 object-contain"
              alt="btn"
            />

            {t("dashboard.addNewCondition")}
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6 p-4 sm:p-6 overflow-y-auto flex-1 w-full">
          {filteredConditions.length > 0 ? (
            filteredConditions.map((healthCondition: IHealthCondition) => (
              <div
                key={healthCondition.id}
                id={healthCondition.id}
                className="relative bg-white rounded-2xl border border-gray-100 shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:shadow-lg hover:border-[#B4804A] transition-all flex flex-col items-center justify-between p-4 sm:p-5 group h-56"
              >
                {/* Top Actions: Edit / Delete */}
                <div className="absolute top-2.5 right-2.5 flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-xs p-1 rounded-md shadow-xs">
                  <button
                    type="button"
                    title="Edit"
                    className="p-1 rounded hover:bg-gray-100 transition-colors"
                    onClick={() => openEditPopup(healthCondition.id)}
                  >
                    <img
                      src="./images/Edit.png"
                      className="w-4 h-4 object-contain"
                      alt="edit"
                    />
                  </button>

                  <button
                    type="button"
                    title="Delete"
                    className="p-1 rounded hover:bg-gray-100 transition-colors"
                    onClick={() => setDeleteItemId(healthCondition.id)}
                  >
                    <img
                      src="./images/delete.png"
                      className="w-4 h-4 object-contain"
                      alt="delete"
                    />
                  </button>

                  {deleteItemId === healthCondition.id && (
                    <DeleteConfirmation
                      onConfirm={() => handleDelete(healthCondition.id)}
                      onCancel={handleCancel}
                      message={healthCondition.name}
                    />
                  )}
                </div>

                {/* Circular Condition Icon */}
                <div className="flex-1 flex items-center justify-center my-auto">
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden flex items-center justify-center bg-gray-50 border border-gray-100 shadow-xs shrink-0">
                    <img
                      src={healthCondition.icon}
                      className="w-full h-full object-cover"
                      alt={healthCondition.name}
                    />
                  </div>
                </div>

                {/* Condition Name */}
                <h4
                  className="mt-2 font-semibold text-[#263238] text-sm sm:text-base font-gilroy capitalize text-center line-clamp-2"
                  title={healthCondition.name}
                >
                  {healthCondition.name}
                </h4>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-12 text-[#78909C] font-medium">
              {t("dashboard.noConditions")}
            </div>
          )}
        </div>

        {isPopupOpen && <Popup closePopup={closePopup} />}

        {isPopupOpenForEdit && editedHealthConditionId && (
          <PopupContentEdit
            closePopups={closeEditPopup}
            existingHealthConditionId={editedHealthConditionId}
          />
        )}
      </div>
    </div>
  );
}